import { useCallback, useEffect, useState } from 'react'
import { Input } from '@heroui/react'
import { api } from '../lib/api'
import { clockOf, minutesOf, policyProblem, policyToSend } from '../lib/bookingAdmin'
import type { BookingPolicy, FieldMode, VisitField } from '../lib/types'
import { formatMoment, nameIn } from '../lib/staffText'
import { explain } from '../lib/staffErrors'
import { useUnsaved } from '../lib/unsaved'
import { useStaffText } from '../ui/StaffText'
import { Messages, useAction } from './parts'
import { Notice, PageHead, SaveActions, Seg, SettingRow, SettingsCard, SwitchRow, UnitNum } from './settingsKit'
import '../styles/branchpage.css'
import '../styles/admin.css'

const MODES: FieldMode[] = ['off', 'optional', 'required']

/**
 * Administration → Online booking → Rules: the organisation's own, saved whole.
 *
 * locked is Administration's bookingLocked: the licence is locked and this
 * organisation still holds the module by its document. The server accepts a
 * save through a lock only when it does nothing but close the test page
 * (onlyClosesTestPage, bookingadmin.go), so every other field is disabled
 * while it stands, and the test-page switch itself only lets it be turned off.
 */
export function BookingRules({ locked }: { locked?: boolean }) {
  const { say, lang } = useStaffText()
  const [p, setP] = useState<BookingPolicy | null>(null)
  const [saved, setSaved] = useState<BookingPolicy | null>(null)
  // The organisation's own fields a booking may ask; none when they cannot be read.
  const [declared, setDeclared] = useState<VisitField[]>([])
  const load = useCallback(async () => {
    const policy = await api.bookingPolicy()
    setP(policy)
    setSaved(policy)
  }, [])
  const { busy, error, notice, run, setError } = useAction(load)
  useEffect(() => {
    load().catch((err: unknown) => {
      setP(null)
      setError(explain(err, lang, 'common.unreachable'))
    })
    api.fields().then(
      (r) => setDeclared(r.fields.filter((f) => f.active)),
      () => setDeclared([]),
    )
  }, [load, lang, setError])
  const dirty = p !== null && JSON.stringify(p) !== JSON.stringify(saved)
  useUnsaved(dirty)

  async function save() {
    if (!p) return
    const problem = policyProblem(p)
    if (problem) return setError(say(problem))
    await run(() => api.saveBookingPolicy(policyToSend(p)), say('common.saved'))
  }

  const head = (
    <PageHead
      title={say('booking.section.rules')}
      line={say('booking.line.rules')}
      actions={p && <SaveActions dirty={dirty} busy={busy} onDiscard={() => setP(saved)} onSave={() => void save()} />}
    />
  )
  if (!p) {
    return (
      <>
        {head}
        <Messages error={error} notice={notice} />
      </>
    )
  }

  const fields = p.fields
  const setFields = (next: Partial<BookingPolicy['fields']>) => {
    const merged = { ...fields, ...next }
    setP({ ...p, fields: merged, fin: merged.fin })
  }
  const setExtra = (key: string, mode: FieldMode) => {
    const extra = { ...fields.extra }
    if (mode === 'off') delete extra[key]
    else extra[key] = mode
    setFields({ extra })
  }
  const min = say('booking.unit.min')
  const num = (label: string, value: number, lo: number, hi: number, set: (v: number) => void, unit?: string, off?: boolean) => (
    <UnitNum label={label} value={value} min={lo} max={hi} unit={unit} disabled={locked || off} onChange={(v) => set(v ?? 0)} />
  )

  return (
    <>
      {head}
      <Messages error={error} notice={notice} />
      {locked && (
        <Notice tone="warning" className="bp-notice">
          {say('booking.locked')}
        </Notice>
      )}

      <SettingsCard title={say('booking.rules.limits')}>
        <SettingRow control label={say('booking.maxOpen')} description={say('booking.rules.maxOpenLine')}>
          {num(say('booking.maxOpen'), p.maxOpenPerContact, 1, 10, (v) => setP({ ...p, maxOpenPerContact: v }))}
        </SettingRow>
        <SettingRow control label={say('booking.maxHolds')} description={say('booking.rules.maxHoldsLine')}>
          {num(say('booking.maxHolds'), p.maxHoldsPerKey, 1, 500, (v) => setP({ ...p, maxHoldsPerKey: v }))}
        </SettingRow>
      </SettingsCard>

      <SettingsCard title={say('booking.rules.arrival')} line={say('booking.rules.arrivalLine')}>
        <SettingRow control label={say('booking.rules.early')} description={say('booking.rules.earlyLine')}>
          {num(say('booking.checkInEarly'), p.checkInEarlyMin, 0, 240, (v) => setP({ ...p, checkInEarlyMin: v }), min)}
        </SettingRow>
        <SettingRow control label={say('booking.rules.onTime')} description={say('booking.rules.onTimeLine')}>
          {num(say('booking.onTime'), p.onTimeMin, 0, 240, (v) => setP({ ...p, onTimeMin: v }), min)}
        </SettingRow>
        <SettingRow control label={say('booking.rules.late')} description={say('booking.rules.lateLine')}>
          {num(say('booking.checkInLate'), p.lateMin, 0, 240, (v) => setP({ ...p, lateMin: v }), min)}
        </SettingRow>
        <SwitchRow
          label={say('booking.bookedFirst')}
          description={say('booking.rules.bookedFirstLine')}
          checked={p.bookedFirst}
          disabled={locked}
          onChange={(bookedFirst) => setP({ ...p, bookedFirst })}
        />
      </SettingsCard>

      <SettingsCard title={say('booking.customerRules')} line={say('booking.rules.customerLine')}>
        <SettingRow
          control
          label={say('booking.rules.changeUntil')}
          description={p.changeUntilHours === 0 ? say('booking.changeUntilNotice') : say('booking.rules.changeUntilLine')}
        >
          {num(say('booking.changeUntil'), p.changeUntilHours, 0, 168, (v) => setP({ ...p, changeUntilHours: v }), say('booking.unit.h'))}
        </SettingRow>
        <SettingRow
          control
          label={say('booking.noShowLimit')}
          description={p.noShowLimit === 0 ? say('booking.rules.noShowOffLine') : say('booking.rules.noShowLine')}
        >
          {num(say('booking.noShowLimit'), p.noShowLimit, 0, 20, (v) => setP({ ...p, noShowLimit: v }))}
        </SettingRow>
        <SettingRow nested control label={say('booking.rules.noShowDays')} disabled={p.noShowLimit === 0}>
          {num(say('booking.noShowDays'), p.noShowDays, 1, 365, (v) => setP({ ...p, noShowDays: v }), say('booking.unit.days'), p.noShowLimit === 0)}
        </SettingRow>
        <SettingRow nested control label={say('booking.rules.noShowBlock')} disabled={p.noShowLimit === 0}>
          {num(say('booking.noShowBlock'), p.noShowBlockDays, 1, 365, (v) => setP({ ...p, noShowBlockDays: v }), say('booking.unit.days'), p.noShowLimit === 0)}
        </SettingRow>
      </SettingsCard>

      <SettingsCard title={say('booking.rules.reminders')}>
        <SwitchRow
          label={say('booking.remindEvening')}
          description={say('booking.rules.remindEveningLine')}
          checked={p.remindDayBeforeAt !== null}
          disabled={locked}
          onChange={(on) => setP({ ...p, remindDayBeforeAt: on ? 18 * 60 : null })}
        />
        {p.remindDayBeforeAt !== null && (
          <SettingRow nested control label={say('booking.rules.sendAt')}>
            <Input
              className="bb-time"
              type="time"
              aria-label={say('booking.remindEvening')}
              value={clockOf(p.remindDayBeforeAt)}
              disabled={locked}
              onChange={(e) => setP({ ...p, remindDayBeforeAt: minutesOf(e.target.value) ?? p.remindDayBeforeAt })}
            />
          </SettingRow>
        )}
        <SwitchRow
          label={say('booking.rules.remindBefore')}
          description={say('booking.rules.remindBeforeLine')}
          checked={p.remindBeforeMin !== null}
          disabled={locked}
          onChange={(on) => setP({ ...p, remindBeforeMin: on ? 60 : null })}
        />
        {p.remindBeforeMin !== null && (
          <SettingRow nested control label={say('booking.rules.before')}>
            {num(say('booking.remindBefore'), p.remindBeforeMin, 5, 1440, (v) => setP({ ...p, remindBeforeMin: v }), min)}
          </SettingRow>
        )}
      </SettingsCard>

      <SettingsCard title={say('booking.rules.pages')}>
        <SettingRow label={say('booking.manageUrl')} description={say('booking.rules.manageUrlLine')}>
          <Input
            fullWidth
            aria-label={say('booking.manageUrl')}
            value={p.manageUrl}
            placeholder={say('booking.problem.manageUrl')}
            disabled={locked}
            onChange={(e) => setP({ ...p, manageUrl: e.target.value })}
          />
        </SettingRow>
        <SwitchRow
          label={say('booking.testPage')}
          description={say('booking.rules.testPageLine')}
          checked={p.testPage}
          // Closing the test page is kept live through a lock; opening one is
          // a write the server refuses with LICENCE_LOCKED.
          disabled={locked && !p.testPage}
          onChange={(testPage) => setP({ ...p, testPage })}
        />
        {p.testPage && (p.testPageUrl || p.testPageUntil) && (
          <SettingRow
            nested
            label={say('booking.rules.testLink')}
            status={
              p.testPageUntil
                ? say('booking.testPageUntil', { date: formatMoment(p.testPageUntil, lang, { date: true, time: true }) })
                : undefined
            }
          >
            {p.testPageUrl && (
              <a className="bb-link" href={p.testPageUrl} target="_blank" rel="noreferrer">
                {p.testPageUrl}
              </a>
            )}
          </SettingRow>
        )}
      </SettingsCard>

      <SettingsCard title={say('booking.fields')} line={say('booking.rules.fieldsLine')}>
        <ModeRow label={say('booking.name')} value={fields.name} onChange={(name) => setFields({ name })} disabled={locked} />
        <ModeRow label={say('booking.mobile')} value={fields.mobile} onChange={(mobile) => setFields({ mobile })} disabled={locked} />
        <ModeRow label={say('booking.contact.email')} value={fields.email} onChange={(email) => setFields({ email })} disabled={locked} />
        <ModeRow label={say('booking.fin')} value={fields.fin} onChange={(fin) => setFields({ fin })} disabled={locked} />
        {declared.map((f) => (
          <ModeRow
            key={f.id}
            label={nameIn(f.name, lang, f.key)}
            value={fields.extra[f.key] ?? 'off'}
            onChange={(mode) => setExtra(f.key, mode)}
            disabled={locked}
          />
        ))}
      </SettingsCard>
    </>
  )
}

/** One customer field: off, optional or required. */
function ModeRow({ label, value, onChange, disabled }: {
  label: string
  value: FieldMode
  onChange: (mode: FieldMode) => void
  disabled?: boolean
}) {
  const { say } = useStaffText()
  return (
    <SettingRow control label={label}>
      <Seg label={label} value={value} disabled={disabled} onChange={onChange} options={MODES.map((m) => ({ value: m, label: say(`booking.fin.${m}`) }))} />
    </SettingRow>
  )
}
